/**
 * Textos e iconos de las justificaciones en Staff (008 · T111; contracts/ui.md §2.2–§2.3).
 *
 * Solo eligen textos a partir de lo que ya decidió el servidor (tipo, estado, minutos observados,
 * causa de cancelación). El lenguaje es neutro: una falta es un hecho del día, no una acusación.
 */
import { Check, CircleMinus, CircleX, Hourglass, type LucideIcon } from 'lucide-react';

import type { JustificationKind, JustificationStatus, StaffDay } from '@fermin/contracts';

import { t } from '../../i18n';
import { weekdayAndDay } from '../schedule/format';

export function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "Tardanza" / "Salida anticipada" / "Falta". */
export function kindLabel(kind: JustificationKind): string {
  return t(`justification.kinds.${kind}`);
}

/** "Tardanza · 12 min" si hay minutos; "Falta" (o el tipo solo) si no. */
export function kindWithMinutes(kind: JustificationKind, minutes: number | null): string {
  if (kind === 'ABSENCE' || minutes === null || minutes <= 0) return kindLabel(kind);
  return t(`justification.kindsWithMinutes.${kind}`, { minutes });
}

/** Minutos del día para el tipo pedido (lo que el servidor ya evaluó); `null` para faltas. */
export function observedMinutesOf(day: StaffDay, kind: JustificationKind): number | null {
  const evaluation = day.evaluation;
  if (evaluation === null || kind === 'ABSENCE') return null;
  return kind === 'LATE' ? evaluation.late_minutes : evaluation.early_leave_minutes;
}

/** "Tardanza del lunes 22". */
export function itemTitle(kind: JustificationKind, day: string): string {
  return capitalize(
    t('justification.section.itemTitle', { kind: kindLabel(kind), day: weekdayAndDay(day) }),
  );
}

export const STATUS_ICON: Record<JustificationStatus, LucideIcon> = {
  PENDING: Hourglass,
  APPROVED: Check,
  REJECTED: CircleX,
  CANCELLED: CircleMinus,
};

export const STATUS_TONE: Record<JustificationStatus, string> = {
  PENDING: 'bg-fm-info-soft text-fm-info-text',
  APPROVED: 'bg-fm-success-soft text-fm-success-text',
  REJECTED: 'bg-fm-danger-soft text-fm-danger-text',
  CANCELLED: 'bg-fm-bg-sunken text-fm-text',
};

export function statusLabel(status: JustificationStatus): string {
  return t(`justification.states.${status}`);
}
